import { motion, AnimatePresence } from 'framer-motion';

export default function AssignmentsConfirmation({
    show,
    onHide,
    onConfirm,
    currentStatus,
    assignmentSubject,
    isUpdating = false
}) {
    const isCompleting = currentStatus !== "Completed";

    // Backdrop animation
    const backdropVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1 }
    };

    // Modal animation
    const modalVariants = {
        hidden: {
            opacity: 0,
            scale: 0.9,
            y: -20
        },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                type: "spring",
                duration: 0.3,
                bounce: 0.2
            }
        },
        exit: {
            opacity: 0,
            scale: 0.9,
            y: 20,
            transition: {
                duration: 0.2
            }
        }
    };

    return (
        <AnimatePresence>
            {show && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                            // backgroundColor: 'rgba(36, 4, 59, 0.75)',
                            backdropFilter: 'blur(4px)',
                            zIndex: 9999
                        }}
                        variants={backdropVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        onClick={!isUpdating ? onHide : undefined}
                    >
                        {/* Modal */}
                        <motion.div
                            className="bg-white rounded-4 shadow-lg"
                            style={{
                                maxWidth: '500px',
                                width: '90%',
                                overflow: 'hidden'
                            }}
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Top Accent Border */}
                            <div style={{
                                height: '5px',
                                background: isCompleting
                                    ? 'var(--success)'
                                    : 'var(--accent)'
                            }} />

                            {/* Header */}
                            <div className="p-4 pb-3 position-relative">
                                {/* Close Button */}
                                <motion.button
                                    type="button"
                                    className="btn-close bg-transparent position-absolute top-0 end-0 m-3"
                                    onClick={onHide}
                                    disabled={isUpdating}
                                    style={{
                                        opacity: isUpdating ? 0.5 : 1,
                                        cursor: isUpdating ? 'not-allowed' : 'pointer'
                                    }}
                                    whileHover={!isUpdating ? { scale: 1.1} : {}}
                                    whileTap={!isUpdating ? { scale: 0.9 } : {}}
                                    aria-label="Close"
                                />

                                <div className='d-flex align-items-center gap-3'>
                                    {/* Icon */}
                                    <div
                                        className="d-inline-flex align-items-center justify-content-center mb-3"
                                        style={{
                                            width: '60px',
                                            height: '60px',
                                            borderRadius: '50%',
                                            backgroundColor: isCompleting
                                                ? 'rgba(16, 185, 129, 0.1)'
                                                : 'rgba(199, 29, 150, 0.1)'
                                        }}
                                    >
                                        <i
                                            className={`fas ${isCompleting ? 'fa-check-circle' : 'fa-arrow-rotate-left'}`}
                                            style={{
                                                fontSize: '2rem',
                                                color: isCompleting ? 'var(--success)' : 'var(--accent)'
                                            }}
                                        />
                                    </div>

                                    {/* Title */}
                                    <h4
                                        className="mb-2"
                                        style={{
                                            color: 'var(--ink)',
                                            fontWeight: '700',
                                            fontSize: '1.5rem'
                                        }}
                                    >
                                        {isCompleting ? 'Mark as Complete?' : 'Reopen Assignment?'}
                                    </h4>

                                </div>

                                {/* Subtitle */}
                                <p
                                    className="mb-0"
                                    style={{
                                        color: 'var(--ink-soft)',
                                        fontSize: '0.95rem',
                                        lineHeight: '1.5'
                                    }}
                                >
                                    {isCompleting
                                        ? 'This will mark the assignment as completed.'
                                        : 'This will reopen the assignment and mark it as not started.'
                                    }
                                </p>
                            </div>

                            {/* Assignment Info Box */}
                            <div
                                className="mx-4 mb-4 p-3 rounded-3"
                                style={{
                                    backgroundColor: 'var(--surface-sunken)',
                                    border: '1px solid var(--hairline)'
                                }}
                            >
                                <small
                                    className="d-block mb-1"
                                    style={{
                                        color: 'var(--ink-soft)',
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    Assignment
                                </small>
                                <div
                                    style={{
                                        color: 'var(--ink)',
                                        fontSize: '0.95rem',
                                        fontWeight: '600'
                                    }}
                                >
                                    {assignmentSubject}
                                </div>
                            </div>

                            {/* Footer */}
                            <div
                                className="p-4 pt-3 d-flex gap-3 justify-content-end"
                                style={{
                                    backgroundColor: 'var(--surface-sunken)',
                                    borderTop: '1px solid var(--hairline)'
                                }}
                            >
                                {/* Cancel Button */}
                                <motion.button
                                    type="button"
                                    className="btn d-flex align-items-center px-4 py-2"
                                    style={{
                                        backgroundColor: 'white',
                                        color: 'var(--ink)',
                                        border: '2px solid var(--hairline)',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        fontSize: '0.95rem'
                                    }}
                                    onClick={onHide}
                                    disabled={isUpdating}
                                    whileHover={!isUpdating ? {
                                        borderColor: 'var(--ink)',
                                        backgroundColor: 'var(--surface-sunken)'
                                    } : {}}
                                    whileTap={!isUpdating ? { scale: 0.98 } : {}}
                                >
                                    Cancel
                                </motion.button>

                                {/* Confirm Button */}
                                <motion.button
                                    type="button"
                                    className="btn d-flex align-items-center px-4 py-2"
                                    style={{
                                        backgroundColor: isCompleting ? 'var(--success)' : 'var(--accent)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        fontSize: '0.95rem',
                                        minWidth: '140px',
                                        justifyContent: 'center',
                                        boxShadow: isCompleting
                                            ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                                            : '0 4px 12px rgba(199, 29, 150, 0.3)'
                                    }}
                                    onClick={onConfirm}
                                    disabled={isUpdating}
                                    whileHover={!isUpdating ? {
                                        scale: 1.02,
                                        backgroundColor: isCompleting ? 'var(--success)' : 'var(--accent-strong)'
                                    } : {}}
                                    whileTap={!isUpdating ? { scale: 0.98 } : {}}
                                >
                                    {isUpdating ? (
                                        <>
                                            <i className="fa-solid fa-spinner fa-spin me-2"></i>
                                            Updating...
                                        </>
                                    ) : isCompleting ? (
                                        <>
                                            <i className="fa-solid fa-check me-2"></i>
                                            Mark Complete
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                                            Reopen
                                        </>
                                    )}
                                </motion.button>
                            </div>
                        </motion.div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}